import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Globe, Check } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { LOCALE_META, type SupportedLocale } from "@/lib/i18n/locales";

export function LanguageSelector() {
  const { locale, setLocale } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Recalculate dropdown position relative to the viewport when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  // Handle clicking outside to dismiss the dropdown panel
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const meta = LOCALE_META[locale] || LOCALE_META.en;

  return (
    <div className="relative pointer-events-auto flex items-center">
      {/* Selector Button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full hover:bg-foreground/5 border border-transparent hover:border-foreground/10 text-foreground/70 hover:text-foreground transition-all group"
        title="Change Language"
      >
        <Globe className="size-4 group-hover:rotate-12 transition-transform duration-200" />
        <span className="text-xs font-mono font-bold leading-none uppercase flex items-center gap-1">
          <span>{meta.flag}</span>
          <span className="hidden sm:inline">{locale}</span>
        </span>
      </button>

      {/* Dropdown Panel rendered at Body level to prevent clipping */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed w-48 rounded-xl bg-[#0e0e16]/95 border border-white/10 shadow-2xl backdrop-blur-xl p-1.5 space-y-0.5 z-[99999] animate-in fade-in zoom-in-95 duration-100 font-mono text-xs text-white"
            style={{ top: `${position.top}px`, right: `${position.right}px` }}
          >
            <div className="px-2 py-1.5 text-[9px] font-bold text-white/45 uppercase tracking-wider border-b border-white/5 mb-1">
              Select Language
            </div>
            {Object.entries(LOCALE_META).map(([code, info]) => {
              const isActive = code === locale;
              return (
                <button
                  key={code}
                  onClick={() => {
                    setLocale(code as SupportedLocale);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-all ${
                    isActive
                      ? "bg-emerald-500/10 text-emerald-400 font-bold"
                      : "hover:bg-white/5 text-white/80 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm shrink-0">{info.flag}</span>
                    <span>{info.native}</span>
                  </span>
                  {isActive && <Check className="size-3.5 text-emerald-400 shrink-0" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
